import { motion } from "framer-motion";
import { FileText, Copy, Zap, Clock } from "lucide-react";
import { CreatorTierBadge, CreatorTier } from "./CreatorTierBadge";
import { cn } from "@/lib/utils";

export type GeoTierLabel = "Verified" | "Strong" | "Good" | "Emerging";

interface GeoSignalsBarProps {
  totalPrompts: number;
  totalCopies: number;
  avgGeoScore: number;
  creatorTier: CreatorTier;
  lastActive: string;
  className?: string;
}

export const getGeoTierLabel = (score: number): GeoTierLabel => {
  if (score >= 90) return "Verified";
  if (score >= 70) return "Strong";
  if (score >= 50) return "Good";
  return "Emerging";
};

export const getGeoTierColors = (label: GeoTierLabel) => {
  switch (label) {
    case "Verified":
      return { text: "text-cyan-400", bg: "bg-cyan-500/10", border: "border-cyan-500/30" };
    case "Strong":
      return { text: "text-purple-400", bg: "bg-purple-500/10", border: "border-purple-500/30" };
    case "Good":
      return { text: "text-blue-400", bg: "bg-blue-500/10", border: "border-blue-500/30" };
    default:
      return { text: "text-muted-foreground", bg: "bg-muted/10", border: "border-muted/30" };
  }
};

export function GeoSignalsBar({
  totalPrompts,
  totalCopies,
  avgGeoScore,
  creatorTier,
  lastActive,
  className
}: GeoSignalsBarProps) {
  const geoTier = getGeoTierLabel(avgGeoScore);
  const geoColors = getGeoTierColors(geoTier);

  const stats = [
    {
      icon: FileText,
      label: "Prompts",
      value: totalPrompts,
    },
    {
      icon: Copy,
      label: "Copies",
      value: totalCopies,
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
      className={cn(
        "flex flex-wrap items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl",
        "bg-card/50 backdrop-blur-sm border border-border/50",
        className
      )}
    >
      {/* Stats */}
      {stats.map((stat, index) => (
        <div key={stat.label} className="flex items-center gap-2 text-sm">
          <stat.icon className="w-4 h-4 text-muted-foreground" />
          <span className="font-semibold text-foreground">{stat.value}</span>
          <span className="text-muted-foreground hidden sm:inline">{stat.label}</span>
        </div>
      ))}

      {/* GEO Tier */}
      <div className="flex items-center gap-2">
        <Zap className="w-4 h-4 text-muted-foreground" />
        <span
          className={cn(
            "px-2 py-0.5 rounded-full text-xs font-medium border",
            geoColors.bg,
            geoColors.border,
            geoColors.text
          )}
        >
          {geoTier} GEO
        </span>
      </div>

      {/* Creator Tier Badge */}
      <div className="hidden sm:block">
        <CreatorTierBadge tier={creatorTier} showIcon={true} />
      </div>

      {/* Last Active */}
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground ml-auto">
        <Clock className="w-3.5 h-3.5" />
        <span>Active {lastActive}</span>
      </div>
    </motion.div>
  );
}
